import { useState } from 'react';
import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { cx } from '@/lib/cx';

/**
 * A colour, picked from the platform picker AND readable as a hex code.
 *
 * ── WHY BOTH HALVES, AND WHY NEITHER ON ITS OWN ─────────────────────────────
 *
 * The picker is a native `<input type="color">` for the reason `Select.tsx`
 * gives about `<select>`: the OS control is accessible, familiar and free,
 * and a hand-rolled colour wheel has to reimplement eyedroppers, recent
 * swatches and keyboard entry, badly. But the STORED value is `#RRGGBB`, and
 * an administrator checking a tab against a written spec needs to read the
 * code and paste it — which a swatch alone cannot do, on any platform. So the
 * hex box is not a convenience beside the picker; it is the half that makes
 * the stored value legible at all.
 *
 * ── THE ONE THING THAT BREAKS SILENTLY ──────────────────────────────────────
 *
 * ⚠️ **A native colour input's `value` must be a valid `#rrggbb` at every
 * moment or the browser resets it to BLACK and reports nothing** — no error,
 * no event, a swatch that has quietly become a colour nobody chose. So the
 * swatch is never handed the in-progress text. It renders `value`, which is
 * the last colour that actually parsed; the half-typed string lives in
 * `draft` and goes no further than the text box.
 *
 * ── TYPING IS NOT FOUGHT, AND IS NOT SILENTLY CORRECTED ─────────────────────
 *
 * While the box has focus the text is whatever was typed — no case folding,
 * no `#` inserted under the cursor, no truncation. Two things happen on the
 * way past:
 *
 *   - the moment the text parses, `onChange` fires with the canonical form, so
 *     the swatch follows along live while the box keeps the raw keystrokes;
 *   - on blur or Enter the text is NORMALISED and the box re-renders from
 *     `value` — uppercase, a leading `#`, a 3-digit shorthand expanded. That
 *     is the only moment the field rewrites itself, and it is a moment the
 *     person has already left.
 *
 * ⛔ **A value that does not parse is REPORTED, never repaired.** `#12345` is
 * not silently padded and `rebecca` is not silently resolved: the box keeps
 * what was typed and paints `aria-invalid`, because a control that turns a
 * typo into a different real colour produces a tab filled in a shade nobody
 * asked for, and nothing anywhere looks wrong. The refusal persists until the
 * text parses or the reset is pressed.
 *
 * ⚠️ **The accepted cost, stated because it is invisible:** a rejected draft
 * leaves `value` on the last colour that parsed, so what is stored and what is
 * on screen differ until the person fixes it. That is why the invalid state is
 * sticky rather than clearing on the next keystroke — the red border is the
 * only thing standing between a typo and a save.
 */

const SHORTHAND = /^[0-9a-fA-F]{3}$/;
const FULL = /^[0-9a-fA-F]{6}$/;

/**
 * `#RRGGBB` for anything a person might reasonably type, `null` for anything
 * else. Accepts a missing `#`, either case, and the 3-digit shorthand; refuses
 * everything else rather than guessing at it.
 *
 * ⚠️ **Deliberately NOT exported.** A caller never needs it: `onChange` only
 * ever emits the canonical form, so there is nothing left for a call site to
 * parse. Exporting it would also make this module export a non-component,
 * which costs the whole file fast refresh — a real loss on a primitive mounted
 * eight times down one screen. Should a second consumer ever appear, this
 * belongs in `src/lib/`, beside the other pure decisions, not re-exported from
 * a component file.
 */
function normaliseHexColor(raw: string): string | null {
  const body = raw.trim().replace(/^#/, '');
  if (SHORTHAND.test(body)) {
    return `#${body
      .split('')
      .map((digit) => digit + digit)
      .join('')
      .toUpperCase()}`;
  }
  if (FULL.test(body)) return `#${body.toUpperCase()}`;
  return null;
}

export interface ColorInputProps {
  /**
   * Lands on the HEX BOX, which is what the wrapping `Field`'s `<label>`
   * points at — the same arrangement `Combobox` uses, where the id goes to the
   * one control a label click should focus.
   */
  id?: string;
  /**
   * Names the swatch and the reset button — the two controls a `Field`'s
   * single `<label htmlFor>` cannot reach.
   *
   * ⚠️ **Not a second visible label, and it renders nowhere.** The visible
   * label is the wrapping `Field`'s. This exists because eight of these on one
   * screen would otherwise give a screen reader eight identically-named
   * pickers and eight identically-named reset buttons, with nothing saying
   * which colour each belonged to.
   */
  label: string;
  /** Always `#RRGGBB`. Anything else renders the swatch black and invalid. */
  value: string;
  /** Only ever called with `#RRGGBB`. */
  onChange: (value: string) => void;
  disabled?: boolean;
  /**
   * The CALLER's refusal — a server 422, say. It is OR-ed with this control's
   * own bad-hex state rather than replacing it, so a field can be refused by
   * both at once and neither answer is dropped.
   */
  invalid?: boolean;
  /** Omitted renders no reset control at all, rather than an inert one. */
  onReset?: () => void;
  /** The reset button's visible word. Required in practice once `onReset` is given. */
  resetLabel?: string;
  'aria-describedby'?: string;
  className?: string;
}

export function ColorInput({
  id,
  label,
  value,
  onChange,
  disabled,
  invalid,
  onReset,
  resetLabel = 'Reset',
  'aria-describedby': describedBy,
  className,
}: ColorInputProps) {
  /** `null` means "not being edited" — the box renders `value` itself. */
  const [draft, setDraft] = useState<string | null>(null);
  /** Set only by a COMMITTED bad value, so a half-typed code is not scolded. */
  const [rejected, setRejected] = useState(false);

  const text = draft ?? value;

  /*
   * The swatch's own value, and the guard described in the header. `value` is
   * by contract already canonical; the fallback covers a caller that is out of
   * contract, and pairs with the invalid flag below so a black swatch over a
   * junk value is never presented as a colour somebody chose.
   */
  const canonical = normaliseHexColor(value);
  const swatch = (canonical ?? '#000000').toLowerCase();

  const showInvalid = Boolean(invalid) || rejected || canonical === null;

  function commit(raw: string) {
    const next = normaliseHexColor(raw);
    if (next === null) {
      // Keep what was typed. Repairing it here is the failure this refuses.
      setRejected(true);
      return;
    }
    setRejected(false);
    setDraft(null);
    if (next !== value) onChange(next);
  }

  function handleText(event: ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value;
    setDraft(raw);
    const next = normaliseHexColor(raw);
    if (next === null) {
      // Mid-type: say nothing. The refusal is a blur-time verdict.
      setRejected(false);
      return;
    }
    /*
     * Parses already — hand it up so the swatch tracks the typing, while the
     * box keeps the raw keystrokes. The normalised form only becomes visible
     * on blur, when `draft` clears.
     */
    setRejected(false);
    if (next !== value) onChange(next);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    if (draft === null) return;
    commit(event.target.value);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      // Commit without submitting whatever form this sits in.
      event.preventDefault();
      commit(event.currentTarget.value);
      return;
    }
    if (event.key === 'Escape' && draft !== null) {
      event.preventDefault();
      setDraft(null);
      setRejected(false);
    }
  }

  function handleSwatch(event: ChangeEvent<HTMLInputElement>) {
    // The native control cannot emit anything but a valid colour.
    setDraft(null);
    setRejected(false);
    onChange(event.target.value.toUpperCase());
  }

  return (
    <div className={cx('flex items-center gap-2', className)}>
      <input
        type="color"
        aria-label={`${label} colour picker`}
        value={swatch}
        onChange={handleSwatch}
        disabled={disabled}
        className={cx(
          'h-9 w-9 shrink-0 cursor-pointer rounded-md border border-border bg-surface p-0 max-sm:h-11 max-sm:w-11',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          'hover:border-hairline-strong',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border',
          /*
           * ⚠️ **The token border is not trim.** The swatch's fill is user
           * data, so it is a literal colour by nature and will sometimes be
           * the page's own ground: at `#FFFFFF` on the light theme and
           * `#111111` on the dark one the control has no edge of its own, and
           * a borderless swatch simply disappears. The border is what keeps it
           * a control at both ends of the range, in both themes, from one
           * token.
           */
          'aria-invalid:border-danger',
          /*
           * The UA draws its own inset padding and border around the swatch,
           * which reads as a second, misaligned frame inside ours. Stripping
           * them lets the fill meet our border; the inner radius sits one step
           * inside `rounded-md` so the corners nest rather than clip.
           */
          '[&::-webkit-color-swatch-wrapper]:p-0',
          '[&::-webkit-color-swatch]:rounded-[3px] [&::-webkit-color-swatch]:border-none',
          '[&::-moz-color-swatch]:rounded-[3px] [&::-moz-color-swatch]:border-none',
        )}
        aria-invalid={showInvalid || undefined}
      />

      {/* min-w-0 so the hex box shrinks rather than pushing the reset off at 320. */}
      <div className="min-w-0 flex-1">
        <Input
          id={id}
          aria-describedby={describedBy}
          value={text}
          onChange={handleText}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          invalid={showInvalid}
          placeholder="#RRGGBB"
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          /*
           * A hex code is read character by character against a written spec,
           * which is what `font-mono` is for here — `0`/`O` and `8`/`B` are
           * the pairs an administrator is checking.
           */
          className="font-mono uppercase"
        />
      </div>

      {onReset && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setDraft(null);
            setRejected(false);
            onReset();
          }}
          disabled={disabled}
          /*
           * The visible word is short enough to fit eight times down a column;
           * the accessible name carries the colour it belongs to, since eight
           * buttons all announcing "Reset to default" name nothing.
           */
          aria-label={`${resetLabel}: ${label}`}
          className="shrink-0"
        >
          {resetLabel}
        </Button>
      )}
    </div>
  );
}
